import { lazy, Suspense, useEffect, type ComponentType } from 'react'
import { Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { LanguageProvider } from '@/lib/language-context'
import { LogoutProvider } from '@/lib/logout-context'
import { PricingLocalizationProvider } from '@/hooks/use-localized-pricing'
import { ErrorBoundary } from '@/components/ErrorBoundary'
import { InitialLoadBar } from '@/components/common/InitialLoadBar'
import { ScrollToTop } from '@/components/common/ScrollToTop'
import { ConditionalLayout } from '@/components/layout/ConditionalLayout'
import { SessionValidator } from '@/components/auth/SessionValidator'
import { captureAwinFromLocation } from '@/lib/awin'
import { captureImpactFromLocation } from '@/lib/impact'

import './index.css'

type ComponentModule<TName extends string> = {
  [key in TName]: ComponentType
}

function logInitialRenderTime() {
  if (!import.meta.env.DEV || typeof performance === 'undefined') return
  requestAnimationFrame(() => {
    console.info(`[perf] initial app render: ${performance.now().toFixed(1)}ms`)
  })
}

function lazyNamed<TName extends string>(
  label: string,
  loader: () => Promise<ComponentModule<TName>>,
  exportName: TName,
) {
  return lazy(async () => {
    const start = import.meta.env.DEV && typeof performance !== 'undefined'
      ? performance.now()
      : 0
    const module = await loader()
    if (import.meta.env.DEV && typeof performance !== 'undefined') {
      console.info(`[perf] lazy load ${label}: ${(performance.now() - start).toFixed(1)}ms`)
    }
    return { default: module[exportName] }
  })
}

const HomePage = lazyNamed('route:/', () => import('@/pages/HomePage'), 'HomePage')
const AdminApp = lazyNamed('route:/admin', () => import('@/admin/AdminApp'), 'AdminApp')
const PricingPage = lazyNamed('route:/pricing', () => import('@/pages/PricingPage'), 'PricingPage')
const ContactPage = lazyNamed('route:/contact', () => import('@/pages/ContactPage'), 'ContactPage')
const FAQPage = lazyNamed('route:/faq', () => import('@/pages/FAQPage'), 'FAQPage')
const NewsPage = lazyNamed('route:/news', () => import('@/pages/NewsPage'), 'NewsPage')
const EventsPage = lazyNamed('route:/events', () => import('@/pages/EventsPage'), 'EventsPage')
const UniversitiesPage = lazyNamed('route:/universities', () => import('@/pages/UniversitiesPage'), 'UniversitiesPage')
const AffiliatePage = lazyNamed('route:/affiliate', () => import('@/pages/AffiliatePage'), 'AffiliatePage')
const ToolsPage = lazyNamed('route:/tools', () => import('@/pages/ToolsPage'), 'ToolsPage')
const ToolDetailPage = lazyNamed('route:/tools/:slug', () => import('@/pages/ToolDetailPage'), 'ToolDetailPage')
const BlogsPage = lazyNamed('route:/blogs', () => import('@/pages/BlogsPage'), 'BlogsPage')
const BlogDetailPage = lazyNamed('route:/blogs/:slug', () => import('@/pages/BlogDetailPage'), 'BlogDetailPage')
const LegalPage = lazyNamed('route:/legal/:slug', () => import('@/pages/LegalPage'), 'LegalPage')
const GetStartedPage = lazyNamed('route:/get-started', () => import('@/pages/GetStartedPage'), 'GetStartedPage')
const LoginPage = lazyNamed('route:/login', () => import('@/pages/LoginPage'), 'LoginPage')
const SignupPage = lazyNamed('route:/signup', () => import('@/pages/SignupPage'), 'SignupPage')
const QuestionnairePage = lazyNamed('route:/questionnaire', () => import('@/pages/QuestionnairePage'), 'QuestionnairePage')
const ForgotPasswordPage = lazyNamed('route:/forgot-password', () => import('@/pages/ForgotPasswordPage'), 'ForgotPasswordPage')
const VerifyOtpPage = lazyNamed('route:/verify-otp', () => import('@/pages/VerifyOtpPage'), 'VerifyOtpPage')
const SSOLoginPage = lazyNamed('route:/sso', () => import('@/pages/SSOLoginPage'), 'SSOLoginPage')
const DashboardLayout = lazyNamed('route:/dashboard', () => import('@/pages/dashboard/DashboardLayout'), 'DashboardLayout')
const DashboardOverview = lazyNamed('route:/dashboard/overview', () => import('@/pages/dashboard/DashboardOverview'), 'DashboardOverview')
const DashboardMyCourses = lazyNamed('route:/dashboard/my-courses', () => import('@/pages/dashboard/DashboardMyCourses'), 'DashboardMyCourses')
const DashboardProfile = lazyNamed('route:/dashboard/profile', () => import('@/pages/dashboard/DashboardProfile'), 'DashboardProfile')
const DashboardCertificates = lazyNamed('route:/dashboard/certificates', () => import('@/pages/dashboard/DashboardCertificates'), 'DashboardCertificates')
const DashboardRewards = lazyNamed('route:/dashboard/rewards', () => import('@/pages/dashboard/DashboardRewards'), 'DashboardRewards')
const DashboardToolsPage = lazyNamed('route:/dashboard/tools', () => import('@/pages/dashboard/DashboardToolsPage'), 'DashboardToolsPage')
const DashboardEventsPage = lazyNamed('route:/dashboard/events', () => import('@/pages/dashboard/DashboardEventsPage'), 'DashboardEventsPage')
const DashboardCommunity = lazyNamed('route:/dashboard/community', () => import('@/pages/dashboard/DashboardCommunity'), 'DashboardCommunity')
const DashboardToolsExplainedPage = lazyNamed('route:/dashboard/tools-explained', () => import('@/pages/dashboard/DashboardToolsExplainedPage'), 'DashboardToolsExplainedPage')
const DashboardCourseDetailPage = lazyNamed('route:/dashboard/courses/:slug', () => import('@/pages/dashboard/DashboardCourseDetailPage'), 'DashboardCourseDetailPage')
const CertificateVerificationPage = lazyNamed('route:/certificates', () => import('@/pages/CertificateVerificationPage'), 'CertificateVerificationPage')
const AuthCallbackPage = lazyNamed('route:/auth/callback', () => import('@/pages/AuthCallbackPage'), 'AuthCallbackPage')
const RedeemPage = lazyNamed('route:/redeem', () => import('@/pages/RedeemPage'), 'RedeemPage')
const NotFoundPage = lazyNamed('route:*', () => import('@/pages/NotFoundPage'), 'NotFoundPage')

const NewsletterPopupController = lazyNamed(
  'widget:NewsletterPopupController',
  () => import('@/components/marketing/NewsletterPopupController'),
  'NewsletterPopupController',
)
const SupportWidget = lazyNamed(
  'widget:SupportWidget',
  () => import('@/components/support/SupportWidget'),
  'SupportWidget',
)
const BackToTopButton = lazyNamed(
  'widget:BackToTopButton',
  () => import('@/components/common/BackToTopButton'),
  'BackToTopButton',
)
const CookieConsentBanner = lazyNamed(
  'widget:CookieConsentBanner',
  () => import('@/components/cookies/CookieConsentBanner'),
  'CookieConsentBanner',
)

function PublicApp() {
  const location = useLocation()

  useEffect(() => {
    captureAwinFromLocation()
    captureImpactFromLocation(location.search)
  }, [location.search])

  return (
    <LanguageProvider>
      <PricingLocalizationProvider>
        <LogoutProvider>
          <InitialLoadBar />
          <ErrorBoundary>
            <SessionValidator />
            <ScrollToTop />
            <ConditionalLayout>
              <Suspense fallback={null}>
                <Routes>
                  <Route path="/" element={<HomePage />} />
                  <Route path="/pricing" element={<PricingPage />} />
                  <Route path="/contact" element={<ContactPage />} />
                  <Route path="/faq" element={<FAQPage />} />
                  <Route path="/news" element={<NewsPage />} />
                  <Route path="/events" element={<EventsPage />} />
                  <Route path="/universities" element={<UniversitiesPage />} />
                  <Route path="/affiliate" element={<AffiliatePage />} />
                  <Route path="/tools" element={<ToolsPage />} />
                  <Route path="/tools/:slug" element={<ToolDetailPage />} />
                  <Route path="/blogs" element={<BlogsPage />} />
                  <Route path="/blogs/:slug" element={<BlogDetailPage />} />
                  <Route path="/legal/:slug" element={<LegalPage />} />
                  <Route path="/get-started" element={<GetStartedPage />} />
                  <Route path="/redeem" element={<RedeemPage />} />
                  <Route path="/referral" element={<Navigate to="/" replace />} />
                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/signup" element={<SignupPage />} />
                  <Route path="/questionnaire" element={<QuestionnairePage />} />
                  <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                  <Route path="/verify-otp" element={<VerifyOtpPage />} />
                  <Route path="/sso" element={<SSOLoginPage />} />
                  <Route path="/auth/callback" element={<AuthCallbackPage />} />
                  <Route path="/certificates" element={<CertificateVerificationPage />} />
                  <Route path="/dashboard" element={<DashboardLayout />}>
                    <Route index element={<Navigate to="/dashboard/overview" replace />} />
                    <Route path="overview" element={<DashboardOverview />} />
                    <Route path="profile/info" element={<DashboardProfile />} />
                    <Route path="profile/settings" element={<DashboardProfile />} />
                    <Route path="profile/referral" element={<Navigate to="/dashboard/profile/info" replace />} />
                    <Route path="profile/subscription" element={<DashboardProfile />} />
                    <Route path="profile/billing" element={<DashboardProfile />} />
                    <Route path="my-courses" element={<DashboardMyCourses />} />
                    <Route path="courses/:slug" element={<DashboardCourseDetailPage />} />
                    <Route path="profile" element={<DashboardProfile />} />
                    <Route path="certificates" element={<DashboardCertificates />} />
                    <Route path="rewards" element={<DashboardRewards />} />
                    <Route path="tools" element={<DashboardToolsPage />} />
                    <Route path="tools/:slug" element={<ToolDetailPage />} />
                    <Route path="events" element={<DashboardEventsPage />} />
                    <Route path="community" element={<DashboardCommunity />} />
                    <Route path="tools-explained" element={<DashboardToolsExplainedPage />} />
                  </Route>
                  <Route path="*" element={<NotFoundPage />} />
                </Routes>
              </Suspense>
            </ConditionalLayout>
            <Suspense fallback={null}>
              <NewsletterPopupController />
            </Suspense>
            <Suspense fallback={null}>
              <SupportWidget />
            </Suspense>
            <Suspense fallback={null}>
              <BackToTopButton />
            </Suspense>
            <Suspense fallback={null}>
              <CookieConsentBanner />
            </Suspense>
          </ErrorBoundary>
        </LogoutProvider>
      </PricingLocalizationProvider>
    </LanguageProvider>
  )
}

function App() {
  useEffect(() => {
    logInitialRenderTime()
  }, [])

  return (
    <Routes>
      <Route path="/admin/*" element={<AdminApp />} />
      <Route path="/*" element={<PublicApp />} />
    </Routes>
  )
}

export default App
